import { useState } from "react";
import {
  BookOpen,
  Layers,
  Lightbulb,
  MonitorPlay,
  PanelLeft,
  X,
} from "lucide-react";
import { useMsal } from "@azure/msal-react";
import {
  approveItem,
  deleteItem,
  listKindMeta,
  listStatusConfig,
} from "../api/graph";
import type { CatalogItem, InitiativeItem, ListKind } from "../types/navigator";
import { AdminTable } from "../components/AdminTable";
import { BannerAdmin } from "../components/BannerAdmin";
import { NavigationAdmin } from "../components/NavigationAdmin";
import { BacklogPriorityModal } from "../components/BacklogPriorityModal";
import { InProgressModal } from "../components/InProgressModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EditItemModal } from "../components/EditItemModal";
import { ItemModal } from "../components/ItemModal";
import { InitiativeReviewActions } from "../components/InitiativeReviewActions";
import { useGraphToken } from "../auth/useGraphToken";
import { useListData } from "../hooks/useListData";

type TabKey = ListKind | "banner" | "navigation";

type TabDef = {
  kind: TabKey;
  label: string;
  icon: typeof BookOpen;
  blurb: string;
};

const TABS: TabDef[] = [
  {
    kind: "initiative",
    label: "Initiatives",
    icon: Lightbulb,
    blurb: "Triage submitted ideas — approve, edit, or remove.",
  },
  {
    kind: "platform",
    label: "Platforms",
    icon: Layers,
    blurb: "Curate the catalog of AI platforms and their approval status.",
  },
  {
    kind: "library",
    label: "Library",
    icon: BookOpen,
    blurb: "Review pending prompts, agents, and tools before they publish.",
  },
  {
    kind: "banner",
    label: "Dashboard banner",
    icon: MonitorPlay,
    blurb: "Choose the videos, images and announcements featured on the dashboard banner.",
  },
  {
    kind: "navigation",
    label: "Navigation",
    icon: PanelLeft,
    blurb: "Arrange the sidebar, add links, and choose who sees each page.",
  },
];

export function AdminPage() {
  const [tab, setTab] = useState<TabKey>("initiative");

  return (
    <div className="page admin-page">
      <div className="page-hero admin-hero">
        <p className="page-subtitle">
          Approve submissions, edit details, and remove items across the AI
          Initiatives, Platforms, and Library lists, manage the dashboard
          banner, and arrange the sidebar navigation.
        </p>
      </div>

      <div className="admin-tabs" role="tablist" aria-label="Catalog">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.kind;
          return (
            <button
              key={t.kind}
              type="button"
              role="tab"
              aria-selected={active}
              className={`admin-tab${active ? " is-active" : ""} admin-tab-${t.kind}`}
              onClick={() => setTab(t.kind)}
            >
              <Icon size={16} strokeWidth={1.9} aria-hidden />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      <p className="admin-tab-blurb">{TABS.find((t) => t.kind === tab)?.blurb}</p>

      {tab === "banner" ? <BannerAdmin /> : tab === "navigation" ? <NavigationAdmin /> : <AdminTabPanel kind={tab} />}
    </div>
  );
}

function AdminTabPanel({ kind }: { kind: ListKind }) {
  const meta = listKindMeta(kind);
  const statusConfig = listStatusConfig(kind);
  const approvedValue = statusConfig.approvedValue;
  const getToken = useGraphToken();
  const { accounts } = useMsal();
  const account = accounts[0];

  // Loads the whole list, so the table, status chart, filters, Next up and
  // In progress all see every item rather than just the first page.
  const { items, loading, error, refresh } = useListData<CatalogItem>({
    listId: meta.listId,
    mapper: meta.mapper,
    search: "",
    filters: {},
  });

  const [view, setView] = useState<CatalogItem | null>(null);
  const [edit, setEdit] = useState<CatalogItem | null>(null);
  const [approving, setApproving] = useState<CatalogItem | null>(null);
  const [deleting, setDeleting] = useState<CatalogItem | null>(null);
  const [reviewing, setReviewing] = useState<CatalogItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);
  const [showBacklog, setShowBacklog] = useState(false);
  const [showInProgress, setShowInProgress] = useState(false);
  // Which popup (if any) opened the current item detail view — so closing
  // the detail view brings that popup back up instead of dropping straight
  // to the plain table.
  const [returnTo, setReturnTo] = useState<"backlog" | "inProgress" | null>(null);

  // Show the freshest copy of the open item, so edits made from inside the
  // popup (a review, a status change) show up without closing it.
  const liveView = view
    ? items.find((i) => i.kind === view.kind && i.id === view.id) ?? view
    : null;

  const closeView = () => {
    setView(null);
    if (returnTo === "backlog") setShowBacklog(true);
    if (returnTo === "inProgress") setShowInProgress(true);
    setReturnTo(null);
  };

  const onApprove = async () => {
    if (!approving) return;
    setBusy(true);
    setOpError(null);
    try {
      const token = await getToken();
      await approveItem(token, approving.kind, approving.id);
      setApproving(null);
      refresh();
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setOpError(null);
    try {
      const token = await getToken();
      await deleteItem(token, deleting.kind, deleting.id);
      setDeleting(null);
      refresh();
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Mount the review host one initiative at a time (launched from the row)
  // so we only fetch the rubric config + saved reviews for the selected
  // initiative, not every visible row.
  const reviewingInitiative =
    reviewing && reviewing.kind === "initiative"
      ? (reviewing as InitiativeItem)
      : null;

  return (
    <>
      {opError ? (
        <div className="error-banner" role="alert">
          <strong>Action failed.</strong> {opError}
        </div>
      ) : null}

      <AdminTable
        kind={kind}
        items={items}
        loading={loading}
        error={error}
        approvedValue={approvedValue}
        onShowNextUp={kind === "initiative" ? () => setShowBacklog(true) : undefined}
        onShowInProgress={kind === "initiative" ? () => setShowInProgress(true) : undefined}
        onView={setView}
        onEdit={setEdit}
        onApprove={setApproving}
        onDelete={setDeleting}
        onReview={kind === "initiative" ? setReviewing : undefined}
      />

      {liveView ? (
        <ItemModal
          item={liveView}
          onClose={closeView}
          onSaved={() => {
            refresh();
            closeView();
          }}
          onReviewSaved={refresh}
        />
      ) : null}

      {showBacklog ? (
        <BacklogPriorityModal
          items={items.filter(
            (i): i is InitiativeItem => i.kind === "initiative"
          )}
          onClose={() => setShowBacklog(false)}
          onSelect={(item) => {
            setShowBacklog(false);
            setReturnTo("backlog");
            setView(item);
          }}
        />
      ) : null}

      {showInProgress ? (
        <InProgressModal
          items={items.filter(
            (i): i is InitiativeItem => i.kind === "initiative"
          )}
          getToken={getToken}
          onMoved={refresh}
          onClose={() => setShowInProgress(false)}
          onSelect={(item) => {
            setShowInProgress(false);
            setReturnTo("inProgress");
            setView(item);
          }}
        />
      ) : null}

      {edit ? (
        <EditItemModal
          item={edit}
          siblings={items}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            refresh();
          }}
        />
      ) : null}

      {reviewingInitiative ? (
        <div
          className="rv-host-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Initiative review"
          onClick={() => setReviewing(null)}
        >
          <div className="rv-host" onClick={(e) => e.stopPropagation()}>
            <div className="rv-host-head">
              <div>
                <span className="rv-host-kind">Review</span>
                <h2 className="rv-host-title">
                  {reviewingInitiative.title || "Untitled initiative"}
                </h2>
              </div>
              <button
                type="button"
                className="rv-host-close"
                onClick={() => setReviewing(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="rv-host-body">
              <InitiativeReviewActions
                initiative={{
                  id: Number(reviewingInitiative.id),
                  title: reviewingInitiative.title,
                  description:
                    reviewingInitiative.problem ||
                    reviewingInitiative.description,
                  submittedBy: reviewingInitiative.submittedBy,
                  businessSponsor: reviewingInitiative.team,
                  created: reviewingInitiative.created,
                  team: reviewingInitiative.team,
                  region: reviewingInitiative.region,
                  problemFrequency: reviewingInitiative.problemFrequency,
                  impactedToday: reviewingInitiative.impactedToday,
                  ifSolved: reviewingInitiative.ifSolved,
                  anticipatedImpact: reviewingInitiative.anticipatedImpact,
                  impactType: reviewingInitiative.impactType,
                  proposedSolution: reviewingInitiative.proposedSolution,
                  existingExamples: reviewingInitiative.existingExamples,
                  dataInputs: reviewingInitiative.dataInputs,
                  constraintsRisks: reviewingInitiative.constraintsRisks,
                  anythingElse: reviewingInitiative.anythingElse,
                }}
                getToken={getToken}
                reviewerUpn={account?.username ?? ""}
                reviewerName={account?.name ?? ""}
                canReview
                // Refresh the table's initiative rows so the Business /
                // Technical / Avg score columns reflect the saved review.
                // The review host stays open so a Business save can unlock
                // the Technical review.
                onReviewSaved={() => refresh()}
              />
              <p className="rv-host-hint">
                Save a Business score to unlock the Technical review.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(approving)}
        title={`Mark ${approvedValue}?`}
        message={
          <>
            <p>
              <strong>{approving?.title || "Untitled"}</strong> will be marked
              as <strong>{approvedValue}</strong> and appear in the public
              catalog.
            </p>
          </>
        }
        confirmLabel={`Mark ${approvedValue}`}
        tone="success"
        busy={busy}
        onConfirm={onApprove}
        onCancel={() => (busy ? null : setApproving(null))}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete item?"
        message={
          <>
            <p>
              <strong>{deleting?.title || "Untitled"}</strong> will be
              permanently removed from SharePoint. This can't be undone from
              the app.
            </p>
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onConfirm={onDelete}
        onCancel={() => (busy ? null : setDeleting(null))}
      />
    </>
  );
}
